/**
 * Stage review console (Back Stage) — the queue that lets a contest entry onto
 * its contest page (founder 2026-09-23: "lets build staff review").
 *
 * An entry arrives `in_review`. Nothing about it is public until someone here
 * approves it: not the entry, not the act's share post (held as a draft until
 * approval, dropped on rejection; worker lib/contest-review.ts).
 *
 * ── What a reviewer is deciding ───────────────────────────────────────
 * Whether the entry is what the contest asked for and is safe to show: the
 * parts are there and playable, nobody under age is in it, the statements the
 * act confirmed are believable against what is on screen. NOT whether it is
 * good; the votes decide that. So the card lays out every part in the
 * contest's own order, the statements as the act saw them, and the credits.
 *
 * ── Music entries are decided elsewhere ───────────────────────────────
 * A music entry is a Postopia Music track plus a performance video, and each
 * goes through the Music queue's rights review. Approving it here would skip
 * that, so music entries are listed read-only with where they stand there.
 *
 * §0c: no window.confirm — reject takes an inline reason, required because
 * the act reads it on their entry and can enter again before entries close.
 *
 * Contests (10.1a H2, 2026-09-24): the page has two sections. Entries (this
 * file) and Contests (components/stage/StageContests: Postopia's own editor
 * plus pause / resume / close for any contest). A hosted contest is announced
 * by its host, not approved here, and its entries are the host's to review, so
 * they are left out of the Entries queue.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { ContestsPane } from '../components/stage/StageContests';
import { BODY, CARD, LABEL, MUTED, when } from '../components/stage/styles';
import { Button } from '../components/ui/Button';
import { PageShell } from '../components/ui/PageShell';
import { TabBar } from '../components/ui/TabBar';
import {
  ContestsAdminError,
  contestsApi,
  entryMediaUrl,
  type EntryMedia,
  type ReviewEntry,
} from '../lib/contestsApi';

export const Route = createFileRoute('/stage')({ component: StageRoute });

type Queue = 'in_review' | 'live' | 'rejected';
type Section = 'entries' | 'contests';

function StageRoute() {
  const [section, setSection] = useState<Section>('entries');

  return (
    <AdminShell mode="feature" featureName="Stage">
      <PageShell
        title="Stage"
        description={
          section === 'entries'
            ? "Approving puts an entry on its contest page and publishes the act's share post. Check that it is what the contest asked for and safe to show, not whether it is good."
            : "Postopia's own contests are written here and announced when ready. Hosts announce their own. Any contest can be paused with a public reason, resumed or closed."
        }
        maxWidth={860}
      >
        <TabBar
          label="Stage sections"
          activeKey={section}
          onSelect={setSection}
          items={[
            { key: 'entries', label: 'Entries' },
            { key: 'contests', label: 'Contests' },
          ]}
        />
        {section === 'entries' ? <EntriesSection /> : <ContestsPane />}
      </PageShell>
    </AdminShell>
  );
}

function EntriesSection() {
  const [queue, setQueue] = useState<Queue>('in_review');
  const [rows, setRows] = useState<ReviewEntry[] | null>(null);
  const [waiting, setWaiting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null);
    contestsApi
      .entries(queue)
      .then((r) => {
        setRows(r.entries);
        setWaiting(r.in_review);
        setError(null);
      })
      .catch((e: unknown) => {
        setRows([]);
        setError(e instanceof ContestsAdminError ? `${e.code}: ${e.message}` : String(e));
      });
  }, [queue]);

  useEffect(load, [load]);

  return (
    <>
        <TabBar
          label="Entry review"
          activeKey={queue}
          onSelect={setQueue}
          items={[
            { key: 'in_review', label: waiting ? `In review (${waiting})` : 'In review' },
            { key: 'live', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
          ]}
        />
        {error ? (
          <p role="alert" style={{ color: 'var(--c-state-danger, #C7563E)', fontSize: 14 }}>
            {error}
          </p>
        ) : null}
        {rows === null ? (
          <p style={{ color: 'var(--c-fg-secondary)', fontSize: 14 }}>Loading…</p>
        ) : rows.length === 0 && !error ? (
          <p style={{ color: 'var(--c-fg-secondary)', fontSize: 14 }}>
            {queue === 'in_review' ? 'No entries are waiting for review.' : queue === 'live' ? 'No approved entries yet.' : 'No rejected entries.'}
          </p>
        ) : (
          rows.map((e) => <EntryCard key={e.id} entry={e} onDecided={load} />)
        )}
    </>
  );
}

function EntryCard({ entry: e, onDecided }: { entry: ReviewEntry; onDecided: () => void }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const name = e.display_name || `@${e.handle}`;
  const round = e.stage_kind ? e.stage_kind.charAt(0).toUpperCase() + e.stage_kind.slice(1) : '';
  const talent = e.talent_label || e.talent_type.replace(/_/g, ' ');
  const byId = new Map(e.media.map((m) => [m.id, m]));
  const specParts = e.entry_spec?.parts ?? [];
  const partKeys = [
    ...specParts.map((p) => p.key).filter((k) => e.parts?.[k]),
    ...Object.keys(e.parts ?? {}).filter((k) => !specParts.some((p) => p.key === k)),
  ];
  const labelFor = (k: string) => specParts.find((p) => p.key === k)?.label ?? k;
  const credits = Object.entries(e.credits ?? {}).filter(([, v]) => v && String(v).trim());

  async function decide(kind: 'approve' | 'reject') {
    setBusy(true);
    setError(null);
    try {
      if (kind === 'approve') await contestsApi.approve(e.id);
      else await contestsApi.reject(e.id, reason.trim());
      onDecided();
    } catch (err) {
      setError(err instanceof ContestsAdminError ? `${err.code}: ${err.message}` : String(err));
      setBusy(false);
    }
  }

  return (
    <article style={CARD} aria-label={`Entry by ${name}`}>
      <header style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {e.avatar_url ? (
          <img src={entryMediaUrl(e.avatar_url) ?? undefined} alt="" width={40} height={40} style={{ borderRadius: 999, objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <span aria-hidden style={{ width: 40, height: 40, borderRadius: 999, background: 'var(--c-bg-surface-alt)', flexShrink: 0 }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <p style={{ ...BODY, fontWeight: 600 }}>
            {name} <span style={{ color: 'var(--c-fg-secondary)', fontWeight: 400 }}>@{e.handle}</span>
          </p>
          <p style={MUTED}>
            {e.contest_title} · {round} · {talent}
          </p>
        </div>
      </header>

      <p style={MUTED}>
        Sent {when(e.created_at)} · entries close {when(e.entries_close_at)} · voting opens {when(e.voting_open_at)}
      </p>

      {e.kind === 'music' ? (
        <div style={{ borderRadius: 8, padding: 12, background: 'var(--c-bg-surface-alt)' }}>
          <p style={BODY}>
            A music entry is decided in the <Link to="/music">Music queue</Link>: the song, then the performance video. It goes live
            when both are approved.
          </p>
          <p style={MUTED}>
            Song: {e.music_submission_status ?? 'unknown'} · Performance video: {e.music_video_status ?? 'not attached yet'}
          </p>
        </div>
      ) : null}

      {partKeys.map((k) => {
        const part = e.parts?.[k];
        if (!part) return null;
        return (
          <section key={k} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={LABEL}>{labelFor(k)}</p>
            {part.text ? (
              <p style={{ ...BODY, whiteSpace: 'pre-wrap', borderLeft: '3px solid var(--c-border-strong)', paddingLeft: 12 }}>{part.text}</p>
            ) : null}
            {part.media_ids?.length ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {part.media_ids.map((id) => {
                  const m = byId.get(id);
                  return m ? <MediaTile key={id} m={m} /> : <p key={id} style={MUTED}>Media {id} is missing or deleted.</p>;
                })}
              </div>
            ) : null}
          </section>
        );
      })}

      {e.kind === 'music' && e.media.length ? (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={LABEL}>Performance video</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
            {e.media.map((m) => (
              <MediaTile key={m.id} m={m} />
            ))}
          </div>
        </section>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={LABEL}>Cover</p>
          {e.cover_url ? (
            <img src={entryMediaUrl(e.cover_url) ?? undefined} alt="Entry cover" style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', borderRadius: 6, background: 'var(--c-bg-surface-alt)' }} />
          ) : (
            <p style={MUTED}>None chosen (the card uses the first photo or a video frame).</p>
          )}
        </section>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <p style={LABEL}>Caption</p>
          <p style={e.caption ? BODY : MUTED}>{e.caption || 'No caption.'}</p>
          <p style={LABEL}>Credits</p>
          {credits.length ? (
            credits.map(([k, v]) => (
              <p key={k} style={BODY}>
                <span style={{ color: 'var(--c-fg-secondary)' }}>{k.replace(/_/g, ' ')}:</span> {v}
              </p>
            ))
          ) : (
            <p style={MUTED}>No credits.</p>
          )}
        </section>
      </div>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={LABEL}>The act confirmed</p>
        {e.declarations.length ? (
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {e.declarations.map((d) => (
              <li key={d.key} style={BODY}>
                {d.text}
              </li>
            ))}
          </ul>
        ) : (
          <p style={MUTED}>Nothing recorded.</p>
        )}
      </section>

      {e.held_shares > 0 && e.status === 'in_review' ? (
        <p style={MUTED}>
          {e.held_shares === 1 ? 'A share post is' : `${e.held_shares} share posts are`} waiting: approving publishes it to the act's followers,
          rejecting deletes it.
        </p>
      ) : null}

      {e.status !== 'in_review' ? (
        <div style={{ borderTop: '1px solid var(--c-border-hairline)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <p style={MUTED}>
            {e.status === 'live' ? 'Approved' : 'Rejected'}
            {e.reviewed_by ? ` by ${e.reviewed_by}` : ''}
            {e.reviewed_at ? `, ${when(e.reviewed_at)}` : ''}
          </p>
          {e.reject_reason ? <p style={BODY}>Reason given to the act: {e.reject_reason}</p> : null}
        </div>
      ) : e.kind !== 'music' ? (
        <div style={{ borderTop: '1px solid var(--c-border-hairline)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rejecting ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ ...BODY, fontWeight: 600 }}>Why is it not accepted? The act reads this and can enter again.</span>
                <textarea
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  rows={3}
                  maxLength={1000}
                  placeholder="For example: someone in the video looks under 18. Re-enter without them."
                  style={{
                    fontSize: 14,
                    padding: 10,
                    borderRadius: 6,
                    border: '1px solid var(--c-border-strong)',
                    background: 'var(--c-bg-page)',
                    color: 'var(--c-fg-default)',
                    resize: 'vertical',
                  }}
                />
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="primary" tone="danger" disabled={busy || !reason.trim()} onClick={() => void decide('reject')}>
                  {busy ? 'Rejecting…' : 'Reject entry'}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setRejecting(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" disabled={busy} onClick={() => void decide('approve')}>
                {busy ? 'Approving…' : 'Approve'}
              </Button>
              <Button variant="secondary" tone="danger" disabled={busy} onClick={() => setRejecting(true)}>
                Reject…
              </Button>
            </div>
          )}
          {error ? (
            <p role="alert" style={{ ...MUTED, color: 'var(--c-state-danger, #C7563E)' }}>
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function MediaTile({ m }: { m: EntryMedia }) {
  const src = entryMediaUrl(m.url);
  const poster = entryMediaUrl(m.poster_url) ?? undefined;
  const flagged = m.scan_status === 'flagged';
  const processing = m.kind === 'video' && m.transcode_status && m.transcode_status !== 'done';
  const box: React.CSSProperties = { width: '100%', borderRadius: 6, background: '#0E0D0C', display: 'block' };
  return (
    <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      {!src ? (
        <p style={MUTED}>No file.</p>
      ) : m.kind === 'video' ? (
        <video src={src} poster={poster} controls preload="metadata" playsInline style={{ ...box, aspectRatio: m.width && m.height ? `${m.width} / ${m.height}` : '9 / 16', maxHeight: 420 }} />
      ) : m.kind === 'audio' ? (
        <audio src={src} controls preload="metadata" style={{ width: '100%' }} />
      ) : (
        <a href={src} target="_blank" rel="noreferrer">
          <img src={src} alt="" style={{ ...box, objectFit: 'contain', aspectRatio: m.width && m.height ? `${m.width} / ${m.height}` : '1 / 1', maxHeight: 420 }} />
        </a>
      )}
      <figcaption style={MUTED}>
        {m.kind}
        {m.duration_ms ? ` · ${Math.round(m.duration_ms / 1000)} s` : ''}
        {m.width && m.height ? ` · ${m.width}×${m.height}` : ''}
        {processing ? ' · still processing' : ''}
        {flagged ? ' · FLAGGED by the scanner' : ''}
      </figcaption>
    </figure>
  );
}
